"use client";

import { LineBlob } from "./LineBlob";

/**
 * AndoBrainLoader — the brand-defining loading indicator.
 *
 * EXPERIMENT (June 2026): renders the clean "pencil-line" `LineBlob` (an orange
 * morphing OUTLINE) in place of the WebGL metaballs (`AndoBrain`), so EVERY wait
 * state across the product (Suspense fallbacks, route transitions, full-page
 * loaders, doc-creation, editor open, canvas) shows the same stylised blob as
 * the login hero / chat streaming indicator. The size/label API is unchanged so
 * no consumer needs editing.
 *
 * Sizes (tuned to common load-state surfaces):
 *  - `sm`  →  64px  (in-page section, inline)
 *  - `md`  →  120px (Suspense default, dialog content)
 *  - `lg`  →  240px (full-page loader, splash screen)
 *
 * Bigger surfaces get a proportionally bolder pencil line. The label is
 * optional — when provided it sits below the blob in muted text.
 *
 * To revert to the metaballs: restore the previous `AndoBrain`-based body from
 * git — `AndoBrain` remains exported and unchanged.
 */

export type AndoBrainLoaderSize = "sm" | "md" | "lg";

export interface AndoBrainLoaderProps {
  size?: AndoBrainLoaderSize;
  label?: string;
  /** Centre-align inside a flexible parent (default true for `lg`, false otherwise). */
  fullscreen?: boolean;
  className?: string;
}

const SIZE_PX: Record<AndoBrainLoaderSize, number> = {
  sm: 64,
  md: 120,
  lg: 240,
};

// Pencil-line stroke scaled to the surface so a big full-page loader reads as the
// same drawing zoomed (bolder line), not a hairline lost on a large blob.
const STROKE_PX: Record<AndoBrainLoaderSize, number> = {
  sm: 1.6,
  md: 2.4,
  lg: 3.4,
};

export function AndoBrainLoader({
  size = "md",
  label,
  fullscreen,
  className,
}: AndoBrainLoaderProps) {
  const px = SIZE_PX[size];
  const wantsFullscreen = fullscreen ?? size === "lg";

  const containerClass = wantsFullscreen
    ? "flex min-h-[60vh] flex-col items-center justify-center gap-4"
    : "inline-flex flex-col items-center justify-center gap-3";

  return (
    <output
      aria-busy="true"
      aria-label={label ?? "Caricamento in corso"}
      className={className ?? containerClass}
    >
      <div aria-hidden className="relative" style={{ width: px, height: px }}>
        <LineBlob size="mini" strokeWidth={STROKE_PX[size]} />
      </div>
      {label ? (
        <span className="font-medium text-gray-500 text-sm tracking-tight">
          {label}
        </span>
      ) : null}
    </output>
  );
}
